<script setup lang="ts">
import { ErrorFallback, ErrorFallbackUnauthorized, ServerError } from '@/components/error'
import { HandledError } from '@/utils/errors'
import * as Sentry from '@sentry/vue'
import { useQueryClient } from '@tanstack/vue-query'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { isAxiosError } from 'axios'
import { usePrimeVue } from 'primevue/config'
import DynamicDialog from 'primevue/dynamicdialog'
import { ref } from 'vue'
import { RouterView } from 'vue-router'
import { LoadingFallback } from '@/components/loading'
import { ErrorType, type WSError } from '@/model/error'
import { useLocalization } from '@/plugins/localization'
import { useRouterStore } from '@/stores/router.store'

const authorized = ref(true)
const routerStore = useRouterStore()
const queryClient = useQueryClient()
queryClient.setDefaultOptions({
  queries: {
    staleTime: 100,
  },
})

function handleError(error: Error) {
  if (!(error instanceof HandledError)) {
    Sentry.captureException(error)
  }
  if (!import.meta.env.PROD && !(error instanceof HandledError)) {
    console.error(error)
  }
  if (isAxiosError(error) && (error.response?.data as WSError)?.type === ErrorType.Unauthorized) {
    authorized.value = false
  }
}

const primevue = usePrimeVue()
const { primevueLocale } = useLocalization()
primevue.config.locale = primevueLocale.value
</script>

<template>
  <LoadingFallback v-if="routerStore.initialLoading" class="h-screen" />
  <ServerError v-else-if="routerStore.serverError" />
  <ErrorBoundary
    stop-propagation
    :fall-back="authorized ? ErrorFallback : ErrorFallbackUnauthorized"
    :params="{ place: 'global' }"
    :on-error="handleError"
  >
    <template #boundary="{ hasError, error }: { hasError: boolean; error: Error }">
      <ErrorFallback v-if="hasError && !(error instanceof HandledError)" />
      <template v-else>
        <DynamicDialog />
        <VueQueryDevtools />
        <RouterView />
      </template>
    </template>
  </ErrorBoundary>
</template>
